import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Grow, Paper } from '@mui/material';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/api';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import { DEFAULT_AFTER_LOGIN_PATH, sanitizeNextPath } from '../utils/redirectUtils';
import { getAuthErrorCode, getAuthErrorMessage } from '../utils/authErrorUtils';
import { AuthResponse } from '../types';

type AuthStep = 'login-phone' | 'login-password' | 'login-otp' | 'register-form' | 'register-otp';
type InputWithIconProps = React.InputHTMLAttributes<HTMLInputElement> & { icon: React.ReactNode; inputClass?: string };
type FieldErrors = Partial<Record<'fullName' | 'phone' | 'email' | 'password' | 'confirmPassword' | 'otp', string>>;
type SubmitButtonProps = {
  loading: boolean;
  disabled?: boolean;
  label: string;
  loadingLabel: string;
  type?: 'button' | 'submit';
  color?: 'primary' | 'secondary';
};

const defaultInputClass =
  'w-full rounded-xl border border-slate-200 bg-white py-2 pr-11 pl-4 text-[13px] leading-6 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 transition-all';

const InputWithIcon: React.FC<InputWithIconProps> = ({ icon, inputClass, ...props }) => (
  <div className="relative">
    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">{icon}</span>
    <input {...props} className={inputClass || defaultInputClass} />
  </div>
);

const SubmitButton: React.FC<SubmitButtonProps> = ({
  loading,
  disabled,
  label,
  loadingLabel,
  type = 'submit',
  color = 'primary',
}) => (
  <Button
    type={type}
    disabled={disabled || loading}
    variant="contained"
    fullWidth
    sx={{
      fontFamily: 'inherit !important',
      textTransform: 'none',
      fontWeight: 700,
      letterSpacing: 0,
      fontSize: '13px',
      borderRadius: '16px',
      minHeight: 42,
      boxShadow: color === 'primary' ? '0 8px 18px rgba(28,59,155,0.28)' : '0 7px 16px rgba(109,40,217,0.24)',
      backgroundColor: color === 'primary' ? '#1C3B9B' : '#6D28D9',
      position: 'relative',
      overflow: 'hidden',
      '@keyframes sendSweep': {
        '0%': { transform: 'translateX(-130%) skewX(-14deg)', opacity: 0 },
        '25%': { opacity: 0.22 },
        '100%': { transform: 'translateX(130%) skewX(-14deg)', opacity: 0 },
      },
      '&:hover': {
        boxShadow: color === 'primary' ? '0 10px 22px rgba(28,59,155,0.34)' : '0 9px 20px rgba(109,40,217,0.3)',
        backgroundColor: color === 'primary' ? '#183387' : '#5B21B6',
      },
      '&.Mui-disabled': {
        color: 'rgba(255,255,255,0.85)',
        backgroundColor: color === 'primary' ? '#93A3D8' : '#C4B5FD',
      },
      '&::after': {
        content: loading ? '""' : 'none',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '44%',
        backgroundColor: 'rgba(255,255,255,0.28)',
        animation: 'sendSweep 980ms cubic-bezier(0.22,1,0.36,1) infinite',
      },
      '& .MuiButton-startIcon, & .MuiButton-endIcon, & span': { fontFamily: 'inherit !important' },
    }}
  >
    <span className="flex items-center justify-center gap-2">
      {loading ? (
        <>
          <BrandSpinnerRing size="xs" inverse />
          <span className="flex items-center gap-1">
            {loadingLabel}
            <span className="inline-flex items-end gap-0.5">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/90" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/90 [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/90 [animation-delay:300ms]" />
            </span>
          </span>
        </>
      ) : (
        label
      )}
    </span>
  </Button>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setSession } = useAuth();

  const isAuthResponse = (response: unknown): response is AuthResponse => {
    if (!response || typeof response !== 'object') return false;
    return 'token' in response && 'user' in response;
  };

  const initialStep = useMemo<AuthStep>(() => {
    const mode = searchParams.get('mode');
    if (mode === 'register' || location.pathname === '/register') return 'register-form';
    return 'login-phone';
  }, [location.pathname, searchParams]);

  /**
   * مقصد بعد از ورود موفق. اولویت با `?next=` است چون رفرش و فوروارد لینک را
   * تحمل می‌کند؛ `state.from` برای صفحاتی نگه داشته شده که قبلاً از آن استفاده می‌کردند.
   */
  const redirectTo = useMemo(() => {
    const fromState = (location.state as { from?: string } | null)?.from;
    return (
      sanitizeNextPath(searchParams.get('next')) ??
      sanitizeNextPath(fromState) ??
      DEFAULT_AFTER_LOGIN_PATH
    );
  }, [searchParams, location.state]);

  const hasRedirectNotice = redirectTo !== DEFAULT_AFTER_LOGIN_PATH;

  /** بعد از ورود، صفحهٔ لاگین نباید در تاریخچهٔ مرورگر بماند */
  const goAfterAuth = useCallback(() => {
    navigate(redirectTo, { replace: true });
  }, [navigate, redirectTo]);

  const [step, setStep] = useState<AuthStep>(initialStep);
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [otpServerError, setOtpServerError] = useState<string | null>(null);
  const [showUnregisteredDialog, setShowUnregisteredDialog] = useState(false);
  const [pendingRegisterPhone, setPendingRegisterPhone] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [phone, setPhone] = useState('');
  const [otpChannel, setOtpChannel] = useState<'sms' | 'bale'>('sms');
  const [loginPassword, setLoginPassword] = useState('');
  const [otp, setOtp] = useState('');
  const otpInputRef = useRef<HTMLInputElement | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [registerForm, setRegisterForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    try {
      const message = sessionStorage.getItem('login_401_message');
      sessionStorage.removeItem('login_401_message');
      if (message && message.trim()) {
        setGlobalError(message.trim());
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const inOtpStep = step === 'login-otp' || step === 'register-otp';
    if (!inOtpStep) {
      setOtpServerError(null);
      setFieldErrors((prev) => ({ ...prev, otp: '' }));
      setGlobalError((prev) => (prev && isOtpRelatedError(prev) ? null : prev));
    }
  }, [step]);

  const setErrorAndShow = (msg: string | null) => {
    setGlobalError(msg);
  };

  const setFieldError = (name: keyof FieldErrors, message?: string) => {
    setFieldErrors((prev) => ({ ...prev, [name]: message || '' }));
  };

  const clearFormErrors = () => {
    setFieldErrors({});
    setGlobalError(null);
    setOtpServerError(null);
  };

  const toUserFriendlyError = (message: string) => {
    const raw = (message || '').trim();
    const lower = raw.toLowerCase();
    if (!raw) return 'خطایی رخ داد. لطفاً دوباره تلاش کنید.';
    if (
      lower.includes('internal server error') ||
      lower.includes('stack') ||
      lower.includes('exception') ||
      lower.includes('prisma') ||
      lower.includes('sql') ||
      lower.includes('database')
    ) {
      return 'مشکلی در سرویس رخ داده است. لطفاً چند لحظه بعد دوباره تلاش کنید.';
    }
    return raw;
  };

  const isOtpRelatedError = (message: string) => {
    const m = (message || '').toLowerCase();
    return m.includes('otp') || m.includes('کد') || m.includes('تایید');
  };
  const isLegacyChannelValidationError = (message: string) => {
    const m = (message || '').toLowerCase();
    return m.includes('property channel should not exist') || m.includes('channel should not exist');
  };

  /**
   * اول کد بک‌اند، بعد متن. تشخیصِ فقط متنی همان چیزی بود که این مسیر را شکسته بود:
   * بک‌اند «اطلاعات ورود نامعتبر است» می‌گفت و هیچ‌کدام از این الگوها را نداشت،
   * پس کاربر ثبت‌نام‌نکرده روی صفحهٔ ورود گیر می‌کرد.
   */
  const isUserNotRegisteredError = (message: string, err?: any) => {
    if (getAuthErrorCode(err) === 'USER_NOT_REGISTERED') return true;
    const m = (message || '').toLowerCase();
    return (
      m.includes('not found') ||
      m.includes('user not found') ||
      m.includes('کاربر یافت نشد') ||
      m.includes('اطلاعات ورود نامعتبر') ||
      m.includes('ثبت نام') ||
      m.includes('ثبت‌نام')
    );
  };

  const redirectToRegisterWithNotice = (rawPhone: string) => {
    const normalizedPhone = normalizePhoneNumber(rawPhone);
    setPendingRegisterPhone(normalizedPhone);
    setShowUnregisteredDialog(true);
  };

  const confirmRedirectToRegister = () => {
    if (pendingRegisterPhone) {
      setPhone(pendingRegisterPhone);
      setRegisterForm((prev) => ({ ...prev, phone: pendingRegisterPhone }));
    }
    setShowUnregisteredDialog(false);
    setPendingRegisterPhone('');
    clearFormErrors();
    setStep('register-form');
  };

  const validatePhoneField = (value: string) => /^09\d{9}$/.test(value.replace(/\D/g, ''));
  const validateOtpField = (value: string) => /^\d{6}$/.test(value);
  const validateEmailField = (value: string) => !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const applyApiErrorToField = (message: string) => {
    const safeMessage = toUserFriendlyError(message);
    const msg = safeMessage.toLowerCase();
    if (msg.includes('phone') || msg.includes('موبایل')) setFieldError('phone', safeMessage);
    else if ((step === 'login-otp' || step === 'register-otp') && (msg.includes('otp') || msg.includes('کد'))) {
      setFieldError('otp', safeMessage);
      return;
    }
    else if (msg.includes('password') || msg.includes('رمز')) setFieldError('password', safeMessage);
    else if (msg.includes('email') || msg.includes('ایمیل')) setFieldError('email', safeMessage);
    setGlobalError(safeMessage);
  };

  const startOtpCountdown = () => {
    setCountdown(120);
    setOtpServerError(null);
  };

  const handleSendOtpForLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFormErrors();
    setErrorAndShow(null);
    if (!phone.trim()) {
      setFieldError('phone', 'شماره موبایل را وارد کنید');
      return;
    }
    if (!validatePhoneField(phone)) {
      setFieldError('phone', 'شماره موبایل باید ۱۱ رقم و با 09 شروع شود');
      return;
    }
    setLoading(true);
    try {
      const normalizedPhone = normalizePhoneNumber(phone);
      // Use login OTP flow endpoint so backend can explicitly reject unregistered users.
      const loginResponse = await authService.login({ login: normalizedPhone });
      if (isAuthResponse(loginResponse)) {
        setSession(loginResponse);
        goAfterAuth();
        return;
      }
      setPhone(normalizedPhone);
      setOtpChannel('sms');
      setOtp('');
      setStep('login-otp');
      startOtpCountdown();
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      if (isUserNotRegisteredError(errorMessage, err)) {
        redirectToRegisterWithNotice(phone);
        return;
      }
      applyApiErrorToField(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtpViaBale = async () => {
    clearFormErrors();
    setErrorAndShow(null);
    if (!phone.trim()) {
      setFieldError('phone', 'شماره موبایل را وارد کنید');
      return;
    }
    if (!validatePhoneField(phone)) {
      setFieldError('phone', 'شماره موبایل باید ۱۱ رقم و با 09 شروع شود');
      return;
    }
    setLoading(true);
    try {
      const normalizedPhone = normalizePhoneNumber(phone);
      try {
        const loginResponse = await authService.login({ login: normalizedPhone, channel: 'bale' });
        if (isAuthResponse(loginResponse)) {
          setSession(loginResponse);
          goAfterAuth();
          return;
        }
        setOtpChannel('bale');
      } catch (innerErr: any) {
        const innerMessage = getAuthErrorMessage(innerErr);
        if (!isLegacyChannelValidationError(innerMessage)) {
          throw innerErr;
        }
        // Backward compatibility for old backend versions without `channel` in LoginDto.
        const fallbackResponse = await authService.login({ login: normalizedPhone });
        if (isAuthResponse(fallbackResponse)) {
          setSession(fallbackResponse);
          goAfterAuth();
          return;
        }
        setOtpChannel('sms');
        setGlobalError('نسخه فعلی سرور از کانال بله پشتیبانی نمی‌کند. کد تایید از طریق پیامک ارسال شد.');
      }
      setPhone(normalizedPhone);
      setOtp('');
      setStep('login-otp');
      startOtpCountdown();
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      if (isUserNotRegisteredError(errorMessage, err)) {
        redirectToRegisterWithNotice(phone);
        return;
      }
      applyApiErrorToField(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLoginOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFormErrors();
    if (!otp.trim()) {
      setFieldError('otp', 'کد تایید را وارد کنید');
      return;
    }
    if (!validateOtpField(otp)) {
      setFieldError('otp', 'کد تایید باید ۶ رقم باشد');
      return;
    }
    setErrorAndShow(null);
    setLoading(true);
    try {
      const response = await authService.verifyOtp(phone, otp);
      setSession(response);
      goAfterAuth();
    } catch (err: any) {
      const safeMessage = toUserFriendlyError(getAuthErrorMessage(err));
      setFieldError('otp', safeMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFormErrors();
    setErrorAndShow(null);
    const loginInput = phone.trim();
    const isEmailInput = loginInput.includes('@');
    if (!loginInput) {
      setFieldError('phone', 'شماره موبایل یا ایمیل را وارد کنید');
      return;
    }
    if (isEmailInput) {
      if (!validateEmailField(loginInput)) {
        setFieldError('phone', 'ایمیل معتبر نیست');
        return;
      }
    } else {
      if (!validatePhoneField(loginInput)) {
        setFieldError('phone', 'شماره موبایل باید ۱۱ رقم و با 09 شروع شود');
        return;
      }
    }
    if (!loginPassword.trim()) {
      setFieldError('password', 'رمز عبور را وارد کنید');
      return;
    }
    setLoading(true);
    try {
      const loginIdentifier = isEmailInput ? loginInput.toLowerCase() : normalizePhoneNumber(loginInput);
      const response = await authService.login({ login: loginIdentifier, password: loginPassword });
      if (!isAuthResponse(response)) {
        throw new Error('Unexpected login response');
      }
      setSession(response);
      goAfterAuth();
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      if (!isEmailInput && isUserNotRegisteredError(errorMessage, err)) {
        redirectToRegisterWithNotice(phone);
        return;
      }
      applyApiErrorToField(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFormErrors();
    setErrorAndShow(null);
    const failRegisterValidation = (name: keyof FieldErrors, message: string) => {
      setFieldError(name, message);
      setErrorAndShow(message);
    };

    if (!registerForm.fullName.trim()) {
      failRegisterValidation('fullName', 'نام و نام خانوادگی را وارد کنید');
      return;
    }
    const normalizedFullName = registerForm.fullName.trim().replace(/\s+/g, ' ');
    if (normalizedFullName.split(' ').length < 2) {
      failRegisterValidation('fullName', 'لطفاً نام و نام خانوادگی را کامل وارد کنید');
      return;
    }

    if (!registerForm.phone.trim()) {
      failRegisterValidation('phone', 'شماره موبایل را وارد کنید');
      return;
    }
    if (!validatePhoneField(registerForm.phone)) {
      failRegisterValidation('phone', 'شماره موبایل باید با 09 شروع شود');
      return;
    }

    if (!validateEmailField(registerForm.email)) {
      failRegisterValidation('email', 'ایمیل معتبر نیست');
      return;
    }

    if (!registerForm.password.trim()) {
      failRegisterValidation('password', 'رمز عبور را وارد کنید');
      return;
    }
    if (registerForm.password.length < 6) {
      failRegisterValidation('password', 'رمز عبور باید حداقل ۶ کاراکتر باشد');
      return;
    }

    if (!registerForm.confirmPassword.trim()) {
      failRegisterValidation('confirmPassword', 'تکرار رمز عبور را وارد کنید');
      return;
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      failRegisterValidation('confirmPassword', 'رمز عبور و تکرار آن یکسان نیست');
      return;
    }

    setLoading(true);
    try {
      const normalizedPhone = normalizePhoneNumber(registerForm.phone);
      const [firstName, ...lastNameParts] = normalizedFullName.split(' ');
      const lastName = lastNameParts.join(' ');

      await authService.register({
        phone: normalizedPhone,
        firstName,
        lastName,
        ...(registerForm.email.trim() ? { email: registerForm.email.trim().toLowerCase() } : {}),
        password: registerForm.password,
        confirmPassword: registerForm.confirmPassword,
        role: 'USER',
      });

      setPhone(normalizedPhone);
      setOtpChannel('sms');
      setOtp('');
      setStep('register-otp');
      startOtpCountdown();
    } catch (err: any) {
      applyApiErrorToField(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegisterOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFormErrors();
    if (!otp.trim()) {
      setFieldError('otp', 'کد تایید را وارد کنید');
      return;
    }
    if (!validateOtpField(otp)) {
      setFieldError('otp', 'کد تایید باید ۶ رقم باشد');
      return;
    }
    setErrorAndShow(null);
    setLoading(true);
    try {
      const response = await authService.verifyOtp(phone, otp);
      setSession(response);
      goAfterAuth();
    } catch (err: any) {
      const safeMessage = toUserFriendlyError(getAuthErrorMessage(err));
      setFieldError('otp', safeMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setErrorAndShow(null);
    setLoading(true);
    try {
      await authService.sendOtp(phone, otpChannel);
      startOtpCountdown();
    } catch (err: any) {
      setOtpServerError('ارسال مجدد کد انجام نشد. اتصال اینترنت را بررسی کنید.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = defaultInputClass;
  const stepIndex = useMemo(() => {
    if (step === 'login-phone') return 1;
    if (step === 'login-password') return 1;
    if (step === 'register-form') return 1;
    if (step === 'login-otp') return 2;
    if (step === 'register-otp') return 3;
    return 1;
  }, [step]);

  const progressSteps = useMemo(() => ['ورود اطلاعات', 'تایید کد', 'تکمیل'], []);

  const IconUser = () => (
    <svg className="h-[18px] w-[18px] text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M20 21a8 8 0 1 0-16 0" />
      <circle cx="12" cy="8" r="4" />
    </svg>
  );

  const IconPhone = () => (
    <svg className="h-[18px] w-[18px] text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2A19.8 19.8 0 0 1 11.2 19a19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1 1 .3 2 .7 2.9a2 2 0 0 1-.5 2L8 9.8a16 16 0 0 0 6.2 6.2l1.2-1.2a2 2 0 0 1 2-.5c.9.4 1.9.6 2.9.7A2 2 0 0 1 22 16.9Z" />
    </svg>
  );

  const IconMail = () => (
    <svg className="h-[18px] w-[18px] text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </svg>
  );

  const IconLock = () => (
    <svg className="h-[18px] w-[18px] text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 1 1 8 0v3" />
    </svg>
  );

  return (
    <div className="relative h-[100dvh] overflow-hidden bg-[#F8FAFC] p-2 sm:p-3">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-indigo-100/40 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-48 w-48 rounded-full bg-fuchsia-100/35 blur-3xl" />
      </div>
      <div className="relative mx-auto flex h-full w-full items-start justify-center pt-2 sm:items-center sm:pt-0">
        <div className="w-full max-w-[310px] sm:max-w-[338px]">
              <Grow in timeout={500}>
                <div className="mb-6 text-center sm:mb-8">
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigate('/')}
                    aria-label="بازگشت به صفحه اصلی"
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      borderRadius: '16px',
                      px: 1,
                      py: 1,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      animation: 'logoFloat 4.8s ease-in-out infinite',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '16px',
                        border: '1px solid rgba(139,92,246,0.35)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: '-35%',
                        width: '26%',
                        transform: 'skewX(-16deg)',
                        background: 'rgba(255,255,255,0.62)',
                        animation: 'muiSheen 3.6s cubic-bezier(0.22, 1, 0.36, 1) infinite',
                      },
                      '&:hover': {
                        transform: 'translateY(-1px)',
                      },
                      '@keyframes logoFloat': {
                        '0%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-4px)' },
                        '100%': { transform: 'translateY(0px)' },
                      },
                      '@keyframes muiSheen': {
                        '0%': { left: '-40%' },
                        '35%': { left: '120%' },
                        '100%': { left: '120%' },
                      },
                    }}
                  >
                    <img
                      src="/assets/logo-mane-haghighi-asli-1 (1).png"
                      alt="Mane Haghighi"
                      className="mx-auto h-12 w-auto object-contain sm:h-20"
                    />
                  </Box>
                </div>
              </Grow>

              <div className="relative mx-auto">
                <div className="absolute -top-2 left-1/2 h-14 w-[103%] -translate-x-1/2 rounded-[14px] border-[8px] border-[#A21CAF] sm:-top-5 sm:h-24 sm:w-[108%] sm:rounded-[20px] sm:border-[18px]" />
                <Grow in timeout={600}>
                  <Paper elevation={2} className="relative max-h-[calc(100dvh-150px)] overflow-y-auto rounded-3xl border border-slate-200/90 bg-white/95 px-3 pb-3 pt-3 shadow-sm backdrop-blur-sm sm:max-h-none sm:overflow-visible sm:px-4 sm:pb-4 sm:pt-4">
                  {hasRedirectNotice ? (
                    <Alert
                      severity="info"
                      icon={false}
                      sx={{
                        mb: 1.5,
                        borderRadius: '12px',
                        fontFamily: 'inherit !important',
                        backgroundColor: '#EEF3FF',
                        border: '1px solid #C8D6FF',
                        color: '#1C3B9B',
                        '& .MuiAlert-message': {
                          fontFamily: 'inherit !important',
                          fontSize: '12px',
                          fontWeight: 700,
                          width: '100%',
                          textAlign: 'center',
                        },
                      }}
                    >
                      {redirectTo.startsWith('/assessment')
                        ? 'برای شرکت در پرسشنامه ابتدا وارد شوید — بلافاصله بعد از ورود به همان صفحه برمی‌گردید.'
                        : 'برای ادامه ابتدا وارد شوید — بلافاصله بعد از ورود به صفحهٔ قبل برمی‌گردید.'}
                    </Alert>
                  ) : null}
                  <Collapse in={!!globalError && step !== 'login-otp' && step !== 'register-otp'}>
                    <Alert
                      severity="error"
                      sx={{
                        mb: 1.5,
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontFamily: 'inherit !important',
                        '& .MuiAlert-message': {
                          fontFamily: 'inherit !important',
                          fontSize: '12px',
                          fontWeight: 500,
                        },
                        '& .MuiAlert-icon': {
                          fontFamily: 'inherit !important',
                        },
                      }}
                    >
                      {globalError}
                    </Alert>
                  </Collapse>
                  <Box
                    sx={{
                      mb: 1.8,
                      px: 0.8,
                      py: 0.75,
                      borderRadius: '14px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, direction: 'rtl' }}>
                      {progressSteps.map((label, idx) => {
                        const nodeStep = idx + 1;
                        const isDone = stepIndex > nodeStep;
                        const isCurrent = stepIndex === nodeStep;
                        return (
                          <Box key={label} sx={{ minWidth: 0, flex: 1, textAlign: 'center' }}>
                            <Box
                              sx={{
                                mx: 'auto',
                                mb: 0.5,
                                width: 16,
                                height: 16,
                                borderRadius: '999px',
                                border: isDone || isCurrent ? '2px solid #1C3B9B' : '2px solid #cbd5e1',
                                backgroundColor: isDone ? '#1C3B9B' : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: 700,
                                boxShadow: isCurrent ? '0 0 0 3px rgba(28,59,155,0.14)' : 'none',
                                transition: 'all 220ms ease',
                              }}
                            >
                              {isDone ? '✓' : ''}
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                fontSize: { xs: '9px', sm: '10.5px' },
                                fontWeight: isCurrent ? 700 : 500,
                                color: isCurrent ? '#0f172a' : '#64748b',
                                transition: 'all 180ms ease',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {label}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    <Box sx={{ position: 'relative', mx: 0.5 }}>
                      <Box sx={{ height: 5, borderRadius: 999, backgroundColor: '#e2e8f0' }} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          height: 5,
                          borderRadius: 999,
                          width: `${((stepIndex - 1) / (progressSteps.length - 1)) * 100}%`,
                          backgroundColor: '#1C3B9B',
                          boxShadow: '0 0 8px rgba(28,59,155,0.25)',
                          transition: 'width 260ms ease',
                        }}
                      />
                    </Box>
                  </Box>

                  <Grow in timeout={220} key={step}>
                  <Box>
                {step === 'login-phone' && (
                <>
                  <div className="space-y-1 text-center">
                    <h1 className="text-lg font-bold text-[#0F172A] sm:text-xl">ورود به حساب کاربری</h1>
                    <p className="text-[13px] text-[#475569]">
                      هنوز ثبت نام نکرده اید؟{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setErrorAndShow(null);
                          setStep('register-form');
                        }}
                        className="font-semibold text-violet-700 hover:text-fuchsia-700"
                      >
                        ثبت نام
                      </button>
                    </p>
                  </div>

                  <form onSubmit={handleSendOtpForLogin} className="mt-2.5 space-y-2 sm:mt-3.5 sm:space-y-2.5">
                    <InputWithIcon
                      type="tel"
                      icon={<IconPhone />}
                      inputClass={`${inputClass} ${fieldErrors.phone ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200' : ''}`}
                      placeholder="شماره موبایل"
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={11}
                      dir="rtl"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value.replace(/\D/g, '').slice(0, 11));
                        if (fieldErrors.phone) setFieldError('phone');
                      }}
                      disabled={loading}
                      required
                    />
                    {fieldErrors.phone ? <p className="text-xs text-rose-500">{fieldErrors.phone}</p> : null}
                    <SubmitButton
                      loading={loading}
                      label="ادامه و ورود به حساب"
                      loadingLabel="در حال ارسال..."
                    />
                    <Button
                      type="button"
                      disabled={loading}
                      variant="outlined"
                      endIcon={<ForumRoundedIcon sx={{ fontSize: 18 }} />}
                      fullWidth
                      onClick={handleSendOtpViaBale}
                      sx={{
                        fontFamily: 'inherit !important',
                        textTransform: 'none',
                        borderRadius: '16px',
                        fontWeight: 600,
                        borderColor: '#86efac',
                        color: '#166534',
                        backgroundColor: '#f0fdf4',
                        justifyContent: 'space-between',
                        '& .MuiButton-endIcon': { marginInlineStart: '8px', marginInlineEnd: 0 },
                        '&:hover': { borderColor: '#4ade80', backgroundColor: '#dcfce7' },
                      }}
                    >
                      ورود با پیام رسان بله
                    </Button>
                    <Button
                      type="button"
                      disabled={loading}
                      variant="outlined"
                      endIcon={<LockOpenRoundedIcon sx={{ fontSize: 18 }} />}
                      fullWidth
                      onClick={() => {
                        clearFormErrors();
                        setStep('login-password');
                      }}
                      sx={{
                        fontFamily: 'inherit !important',
                        textTransform: 'none',
                        borderRadius: '16px',
                        fontWeight: 600,
                        borderColor: '#cbd5e1',
                        color: '#334155',
                        backgroundColor: '#f8fafc',
                        justifyContent: 'space-between',
                        '& .MuiButton-endIcon': { marginInlineStart: '8px', marginInlineEnd: 0 },
                        '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f1f5f9' },
                      }}
                    >
                      ورود با رمز عبور
                    </Button>
                  </form>
                </>
                )}

                {step === 'login-password' && (
                <>
                  <div className="space-y-1 text-center">
                    <h1 className="text-lg font-bold text-[#0F172A] sm:text-xl">ورود با رمز عبور</h1>
                    <p className="text-[13px] text-[#475569]">شماره موبایل یا ایمیل و رمز عبور خود را وارد کنید.</p>
                  </div>

                  <form onSubmit={handlePasswordLogin} className="mt-2.5 space-y-2 sm:mt-3.5 sm:space-y-2.5">
                    <InputWithIcon
                      type="text"
                      icon={phone.includes('@') ? <IconMail /> : <IconPhone />}
                      inputClass={`${inputClass} ${fieldErrors.phone ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200' : ''}`}
                      placeholder="شماره موبایل یا ایمیل"
                      autoComplete="username"
                      dir="ltr"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        if (fieldErrors.phone) setFieldError('phone');
                      }}
                      disabled={loading}
                      required
                    />
                    {fieldErrors.phone ? <p className="text-xs text-rose-500">{fieldErrors.phone}</p> : null}

                    <InputWithIcon
                      type="password"
                      icon={<IconLock />}
                      inputClass={`${inputClass} ${fieldErrors.password ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200' : ''}`}
                      placeholder="رمز عبور"
                      autoComplete="current-password"
                      dir="rtl"
                      value={loginPassword}
                      onChange={(e) => {
                        setLoginPassword(e.target.value);
                        if (fieldErrors.password) setFieldError('password');
                      }}
                      disabled={loading}
                      required
                    />
                    {fieldErrors.password ? <p className="text-xs text-rose-500">{fieldErrors.password}</p> : null}

                    <SubmitButton
                      loading={loading}
                      label="ورود به حساب"
                      loadingLabel="در حال ورود..."
                    />
                    <button
                      type="button"
                      onClick={() => {
                        clearFormErrors();
                        setStep('login-phone');
                      }}
                      className="w-full text-[13px] text-[#475569] hover:text-[#0F172A]"
                    >
                      بازگشت به ورود با کد
                    </button>
                  </form>
                </>
                )}

                {(step === 'login-otp' || step === 'register-otp') && (
                <>
                  <div className="space-y-1 text-center">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        borderRadius: '999px',
                        px: 1.2,
                        py: 0.5,
                        mb: 0.5,
                        backgroundColor: '#eff6ff',
                        color: '#1d4ed8',
                        fontSize: '11px',
                        fontWeight: 700,
                        '@keyframes otpBadgeSlideIn': {
                          '0%': { opacity: 0, transform: 'translateX(-14px)' },
                          '100%': { opacity: 1, transform: 'translateX(0)' },
                        },
                        animation: 'otpBadgeSlideIn 360ms ease-out',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3l8 4v5c0 5-3.4 8.5-8 9-4.6-.5-8-4-8-9V7l8-4z" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                      کد امنیتی
                    </Box>
                    <h1 className="text-lg font-bold text-[#0F172A] sm:text-xl">تایید شماره موبایل</h1>
                    <p className="text-[13px] text-[#475569]">کد تایید ارسال‌شده به {phone} را وارد کنید.</p>
                  </div>

                  <form
                    onSubmit={step === 'login-otp' ? handleVerifyLoginOtp : handleVerifyRegisterOtp}
                    className="mt-2.5 space-y-2 sm:mt-3.5 sm:space-y-2.5"
                  >
                    <div
                      className="relative"
                      onClick={() => {
                        otpInputRef.current?.focus();
                      }}
                    >
                      <input
                        ref={otpInputRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        dir="ltr"
                        maxLength={6}
                        className="absolute inset-0 z-10 h-11 w-full cursor-text rounded-xl bg-transparent text-left font-mono text-transparent caret-blue-600 outline-none"
                        value={otp}
                        onChange={(e) => {
                          setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                          if (fieldErrors.otp) setFieldError('otp');
                          if (otpServerError) setOtpServerError(null);
                        }}
                        disabled={loading}
                        required
                      />
                      <div className="pointer-events-none grid grid-cols-6 gap-2" dir="ltr">
                        {Array.from({ length: 6 }).map((_, idx) => (
                          <div
                            key={idx}
                            className={`flex h-11 items-center justify-center rounded-xl border text-lg font-semibold transition-all ${
                              fieldErrors.otp
                                ? 'border-rose-300 bg-rose-50 text-rose-600'
                                : otp.length > idx
                                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                                  : 'border-slate-200 bg-white text-slate-400'
                            }`}
                          >
                            {otp[idx] || '-'}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Collapse in={!!fieldErrors.otp}>
                        <div className="flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-700">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M12 8v5" />
                            <path d="M12 16h.01" />
                          </svg>
                          {fieldErrors.otp}
                        </div>
                      </Collapse>
                      <Collapse in={!!otpServerError}>
                        <div className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-700">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 9v4" />
                            <path d="M12 17h.01" />
                            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                          </svg>
                          {otpServerError}
                        </div>
                      </Collapse>
                    </div>
                    <SubmitButton
                      loading={loading}
                      label="تایید و ادامه"
                      loadingLabel="در حال تایید..."
                    />
                  </form>

                  <div className="text-center">
                    {countdown > 0 ? (
                      <p className="text-[13px] text-[#475569]">
                        ارسال مجدد تا {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={loading}
                        className="text-[13px] font-medium text-violet-700 hover:text-fuchsia-700"
                      >
                        ارسال مجدد کد
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setOtp('');
                      setErrorAndShow(null);
                      setStep(step === 'login-otp' ? 'login-phone' : 'register-form');
                    }}
                    className="w-full text-[13px] text-[#475569] hover:text-[#0F172A]"
                  >
                    بازگشت
                  </button>
                </>
                )}

                {step === 'register-form' && (
                <>
                  <div className="space-y-1 text-center">
                    <h1 className="text-lg font-bold text-[#0F172A] sm:text-xl">ساخت حساب کاربری جدید</h1>
                    <p className="text-[13px] text-[#475569]">
                      قبلاً ثبت نام کرده اید؟{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setErrorAndShow(null);
                          setStep('login-phone');
                        }}
                        className="font-semibold text-violet-700 hover:text-fuchsia-700"
                      >
                        ورود
                      </button>
                    </p>
                    <p className="text-[11px] text-slate-400">برای شروع، اطلاعات اصلی را وارد کنید</p>
                  </div>

                  <form onSubmit={handleRegisterSubmit} className="mt-2.5 space-y-1.5 sm:mt-3 sm:space-y-2">
                    <InputWithIcon
                      type="text"
                      icon={<IconUser />}
                      inputClass={`${inputClass} ${fieldErrors.fullName ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200' : ''}`}
                      placeholder="* نام و نام خانوادگی"
                      autoComplete="name"
                      dir="rtl"
                      value={registerForm.fullName}
                      onChange={(e) => {
                        setRegisterForm((prev) => ({ ...prev, fullName: e.target.value }));
                        if (fieldErrors.fullName) setFieldError('fullName');
                      }}
                      disabled={loading}
                      required
                    />
                    {fieldErrors.fullName ? <p className="text-xs text-rose-500">{fieldErrors.fullName}</p> : null}

                    <InputWithIcon
                      type="tel"
                      icon={<IconPhone />}
                      inputClass={`${inputClass} ${fieldErrors.phone ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200' : ''}`}
                      placeholder="* شماره موبایل"
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={11}
                      dir="rtl"
                      value={registerForm.phone}
                      onChange={(e) => {
                        setRegisterForm((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 11) }));
                        if (fieldErrors.phone) setFieldError('phone');
                      }}
                      disabled={loading}
                      required
                    />
                    {fieldErrors.phone ? <p className="text-xs text-rose-500">{fieldErrors.phone}</p> : null}

                    <InputWithIcon
                      type="email"
                      icon={<IconMail />}
                      inputClass={`${inputClass} ${fieldErrors.email ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200' : ''}`}
                      placeholder="آدرس ایمیل (اختیاری)"
                      autoComplete="email"
                      dir="rtl"
                      value={registerForm.email}
                      onChange={(e) => {
                        setRegisterForm((prev) => ({ ...prev, email: e.target.value }));
                        if (fieldErrors.email) setFieldError('email');
                      }}
                      disabled={loading}
                    />
                    {fieldErrors.email ? <p className="text-xs text-rose-500">{fieldErrors.email}</p> : null}

                    <InputWithIcon
                      type="password"
                      icon={<IconLock />}
                      inputClass={`${inputClass} ${fieldErrors.password ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200' : ''}`}
                      placeholder="رمز عبور"
                      autoComplete="new-password"
                      dir="rtl"
                      value={registerForm.password}
                      onChange={(e) => {
                        setRegisterForm((prev) => ({ ...prev, password: e.target.value }));
                        if (fieldErrors.password) setFieldError('password');
                      }}
                      disabled={loading}
                      required
                    />
                    {fieldErrors.password ? <p className="text-xs text-rose-500">{fieldErrors.password}</p> : null}

                    <InputWithIcon
                      type="password"
                      icon={<IconLock />}
                      inputClass={`${inputClass} ${fieldErrors.confirmPassword ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200' : ''}`}
                      placeholder="تکرار رمز عبور"
                      autoComplete="new-password"
                      dir="rtl"
                      value={registerForm.confirmPassword}
                      onChange={(e) => {
                        setRegisterForm((prev) => ({ ...prev, confirmPassword: e.target.value }));
                        if (fieldErrors.confirmPassword) setFieldError('confirmPassword');
                      }}
                      disabled={loading}
                      required
                    />
                    {fieldErrors.confirmPassword ? <p className="text-xs text-rose-500">{fieldErrors.confirmPassword}</p> : null}

                    <SubmitButton
                      loading={loading}
                      label="ادامه و ثبت نام"
                      loadingLabel="در حال ثبت‌نام..."
                    />
                  </form>
                  <div className="mt-2 hidden items-center justify-center gap-3 text-[11px] text-slate-400 sm:flex">
                    <span>ورود امن</span>
                    <span>•</span>
                    <span>حفظ حریم خصوصی</span>
                    <span>•</span>
                    <span>پشتیبانی سریع</span>
                  </div>
                </>
                )}
                  </Box>
                  </Grow>
                  </Paper>
                </Grow>
                {(step === 'login-phone' || step === 'login-password') && (
                  <p className="mt-3 px-1 text-center text-[12px] text-[#475569] sm:text-[13px]">
                    ورود به حساب کاربری به منزله پذیرش
                    {' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-700 hover:text-fuchsia-700">
                      قوانین و مقررات
                    </Link>
                    {' '}
                    و
                    {' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-700 hover:text-fuchsia-700">
                      حریم خصوصی
                    </Link>
                    {' '}
                    و مطالعهٔ آن‌هاست.
                  </p>
                )}
                {step === 'register-form' && (
                  <p className="mt-3 px-1 text-center text-[12px] text-[#475569] sm:text-[13px]">
                    با عضویت در سایت،
                    {' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-700 hover:text-fuchsia-700">
                      قوانین
                    </Link>
                    {' '}
                    و
                    {' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-700 hover:text-fuchsia-700">
                      حریم خصوصی
                    </Link>
                    {' '}
                    را می‌پذیرید.
                  </p>
                )}
              </div>
        </div>
        </div>

      <Dialog
        open={showUnregisteredDialog}
        onClose={() => setShowUnregisteredDialog(false)}
        fullWidth
        maxWidth="xs"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '14px',
            fontFamily: 'inherit !important',
            mx: 1.5,
          },
          '& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root, & .MuiButton-root': {
            fontFamily: 'inherit !important',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 700, pb: 1 }}>ثبت‌نام موردنیاز است</DialogTitle>
        <DialogContent sx={{ pt: '6px !important' }}>
          <p className="text-[13px] text-slate-600">این شماره در سیستم ثبت‌نام نشده است. لطفاً ابتدا ثبت‌نام کنید.</p>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={confirmRedirectToRegister}
            variant="contained"
            sx={{
              borderRadius: '10px',
              backgroundColor: '#1C3B9B',
              '&:hover': { backgroundColor: '#183387' },
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: 'inherit !important',
            }}
          >
            تایید
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default Login;
